import React, { useEffect, useRef, useState } from 'react';
import { extractPeaks, seedBars, cropAudio, formatTime } from './audioUtils';

const BAR_COUNT = 58;
const ACCENT = '#BFA253';
const ACCENT_DIM = '#c5c5c5';
const HANDLE_W = 4;

const OnsiteAudioCropper = ({ src, onCropDone, onCancel }) => {
  const [bars, setBars] = useState(() => seedBars(src));
  const [duration, setDuration] = useState(0);
  const [startBar, setStartBar] = useState(0);
  const [endBar, setEndBar] = useState(BAR_COUNT - 1);
  const [dragging, setDragging] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [wavBuffer, setWavBuffer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playheadProgress, setPlayheadProgress] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmGenerating, setConfirmGenerating] = useState(false);

  const containerRef = useRef(null);
  const audioRef = useRef(null);
  const draggingRef = useRef(null);
  const startBarRef = useRef(0);
  const endBarRef = useRef(BAR_COUNT - 1);
  const previewUrlRef = useRef('');
  const rafRef = useRef(null);
  const generatingRef = useRef(false); // prevent concurrent runs
  const doGenerateRef = useRef(null);  // stable ref to latest generate fn

  startBarRef.current = startBar;
  endBarRef.current = endBar;

  // Decode waveform + source duration on mount
  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    const load = async () => {
      try {
        const peaks = await extractPeaks(src);
        if (!cancelled) setBars(peaks);
      } catch { /* keep seeded fallback */ }
      try {
        const ab = await fetch(src).then((r) => r.arrayBuffer());
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const decoded = await ctx.decodeAudioData(ab);
        ctx.close();
        if (!cancelled) setDuration(decoded.duration);
      } catch { /* duration stays 0 */ }
    };
    load();
    return () => { cancelled = true; };
  }, [src]);

  // Stable generate fn — stored in ref so drag handler always calls latest version.
  // Called on mount and after each drag release. Keeps wavBuffer pre-loaded so
  // handlePlayPause is fully synchronous (no async gap, no autoplay block).
  doGenerateRef.current = async () => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    setIsGenerating(true);
    try {
      const startRatio = startBarRef.current / BAR_COUNT;
      const endRatio = (endBarRef.current + 1) / BAR_COUNT;
      const wav = await cropAudio(src, startRatio, endRatio);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const blob = new Blob([wav], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setWavBuffer(wav);
      setPlayheadProgress(0);
      if (audioRef.current) { audioRef.current.src = url; audioRef.current.load(); }
    } catch (err) {
      console.error('[AudioCropper] generate failed:', err);
    } finally {
      generatingRef.current = false;
      setIsGenerating(false);
    }
  };

  // Generate on mount
  useEffect(() => {
    doGenerateRef.current();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Revoke preview blob on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  // Wire audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const tick = () => {
      const d = audio.duration;
      if (Number.isFinite(d) && d > 0) setPlayheadProgress(Math.min(1, audio.currentTime / d));
      rafRef.current = requestAnimationFrame(tick);
    };
    const onPlay = () => { setIsPlaying(true); rafRef.current = requestAnimationFrame(tick); };
    const onPause = () => { setIsPlaying(false); cancelAnimationFrame(rafRef.current); };
    const onEnded = () => { setIsPlaying(false); cancelAnimationFrame(rafRef.current); setPlayheadProgress(1); };
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    return () => {
      cancelAnimationFrame(rafRef.current);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  // Global drag listeners
  useEffect(() => {
    const handleMove = (clientX) => {
      if (!draggingRef.current) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const idx = Math.max(0, Math.min(BAR_COUNT - 1, Math.round((clientX - rect.left) / rect.width * BAR_COUNT)));
      if (draggingRef.current === 'start') setStartBar(Math.min(idx, endBarRef.current - 1));
      else setEndBar(Math.max(idx, startBarRef.current + 1));
    };

    const stopAudio = () => {
      // Explicitly reset playing state — src change doesn't reliably fire pause event
      cancelAnimationFrame(rafRef.current);
      if (audioRef.current) audioRef.current.pause();
      setIsPlaying(false);
      setPlayheadProgress(0);
      setWavBuffer(null);
    };

    const onMouseMove = (e) => handleMove(e.clientX);
    const onTouchMove = (e) => { e.preventDefault(); handleMove(e.touches[0].clientX); };
    const onUp = () => {
      if (draggingRef.current) {
        stopAudio();
        doGenerateRef.current(); // re-generate for new crop region
      }
      draggingRef.current = null;
      setDragging(null);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchend', onUp);
    };
  }, []);

  const startDrag = (which) => { draggingRef.current = which; setDragging(which); };

  // Fully synchronous — wavBuffer always pre-generated, no async gap, no autoplay block
  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || isGenerating) return;
    if (isPlaying) { audio.pause(); return; }
    audio.play().catch(() => {});
  };

  const handleConfirmCrop = async () => {
    setConfirmGenerating(true);
    try {
      let wav = wavBuffer;
      if (!wav) {
        const startRatio = startBar / BAR_COUNT;
        const endRatio = (endBar + 1) / BAR_COUNT;
        wav = await cropAudio(src, startRatio, endRatio);
      }
      onCropDone(new Blob([wav], { type: 'audio/wav' }));
    } catch (err) {
      console.error('[AudioCropper] crop failed:', err);
      setConfirmGenerating(false);
      setShowConfirm(false);
    }
  };

  const handleCancel = () => {
    cancelAnimationFrame(rafRef.current);
    if (audioRef.current) audioRef.current.pause();
    if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = ''; }
    onCancel();
  };

  // Layout — percentage-based so waveform fills container at any width
  const BAR_GAP = 3;
  const startPct = (startBar / BAR_COUNT) * 100;
  const endPct = ((endBar + 1) / BAR_COUNT) * 100;
  const cropStart = duration > 0 ? (startBar / BAR_COUNT) * duration : 0;
  const cropEnd = duration > 0 ? ((endBar + 1) / BAR_COUNT) * duration : 0;
  const cropDur = cropEnd - cropStart;

  const handleStyle = (pct, offsetPx = 0) => ({
    position: 'absolute', top: 0, bottom: 0,
    left: `calc(${pct}% + ${offsetPx}px)`, width: `${HANDLE_W}px`,
    background: '#2c2c2c', cursor: 'col-resize', zIndex: 2,
  });

  const gripTriangle = (pos) => ({
    position: 'absolute', [pos]: '-11px',
    left: '50%', transform: 'translateX(-50%)',
    width: 0, height: 0,
    borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
    ...(pos === 'top' ? { borderTop: '11px solid #2c2c2c' } : { borderBottom: '11px solid #2c2c2c' }),
  });

  const btnBase = {
    border: 'none', borderRadius: '10px', padding: '11px 22px', fontSize: '14px',
    fontWeight: 600, cursor: 'pointer', fontFamily: '"Poppins", sans-serif',
    display: 'inline-flex', alignItems: 'center', gap: '7px',
  };

  const playIcon = isGenerating ? 'fa-spinner fa-spin' : isPlaying ? 'fa-pause' : 'fa-play';
  const playLabel = isGenerating ? 'Loading…' : isPlaying ? 'Pause' : 'Play';

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1050,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#faf7f0', borderRadius: '20px', padding: '32px 32px 28px',
        maxWidth: '820px', width: '92%', boxShadow: '0 12px 48px rgba(0,0,0,0.18)',
        border: '1.5px solid rgba(92,69,24,0.14)',
        fontFamily: '"Poppins", sans-serif',
      }}>
        <div style={{ marginBottom: '4px' }}>
          <span style={{ fontSize: '18px', fontWeight: 700, color: '#2a2418' }}>Crop Recording</span>
        </div>
        <p style={{ fontSize: '13px', color: '#8a7d6a', margin: '2px 0 24px' }}>
          Drag handles to select region
        </p>

        {/* Waveform + handles */}
        <div
          ref={containerRef}
          style={{
            position: 'relative', height: '110px',
            width: '100%',
            userSelect: 'none', cursor: dragging ? 'col-resize' : 'default',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', height: '100%', gap: `${BAR_GAP}px` }}>
            {bars.map((h, i) => {
              const inside = i >= startBar && i <= endBar;
              return (
                <span key={i} style={{
                  flex: '1 1 0',
                  height: `${Math.round(64 * h)}px`, borderRadius: '3px',
                  background: inside ? ACCENT : ACCENT_DIM,
                }} />
              );
            })}
          </div>

          {/* Playhead */}
          {wavBuffer && (
            <div style={{
              position: 'absolute', top: 0, bottom: 0,
              left: `${startPct + playheadProgress * (endPct - startPct)}%`,
              width: '2px', background: '#2c2c2c', opacity: 0.75,
              pointerEvents: 'none', zIndex: 3,
            }} />
          )}

          <div style={handleStyle(startPct)}
            onMouseDown={(e) => { e.preventDefault(); startDrag('start'); }}
            onTouchStart={(e) => { e.preventDefault(); startDrag('start'); }}>
            <div style={gripTriangle('top')} />
            <div style={gripTriangle('bottom')} />
          </div>

          <div style={handleStyle(endPct, -HANDLE_W)}
            onMouseDown={(e) => { e.preventDefault(); startDrag('end'); }}
            onTouchStart={(e) => { e.preventDefault(); startDrag('end'); }}>
            <div style={gripTriangle('top')} />
            <div style={gripTriangle('bottom')} />
          </div>
        </div>

        {/* Time labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px', fontSize: '12px', color: '#8a7d6a' }}>
          <span>Start: {formatTime(Math.floor(cropStart))}</span>
          {duration > 0 && <span style={{ color: ACCENT, fontWeight: 600 }}>Duration: {formatTime(Math.floor(cropDur))}</span>}
          <span>End: {formatTime(Math.floor(cropEnd))}</span>
        </div>

        <audio ref={audioRef} style={{ display: 'none' }} />

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '28px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handlePlayPause}
            disabled={isGenerating}
            aria-label={playLabel}
            style={{
              width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
              border: '1px solid #d6d6d6', background: '#fff',
              color: '#666', cursor: isGenerating ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <i className={`fa-solid ${playIcon}`} style={{ fontSize: 11 }} />
          </button>

          <div style={{ flex: 1 }} />

          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            style={{ ...btnBase, background: ACCENT, color: '#fff' }}
          >
            <i className="fa-solid fa-check" />Use Crop
          </button>

          <button type="button" onClick={handleCancel}
            style={{ ...btnBase, background: 'none', border: '1px solid rgba(92,69,24,0.2)', color: '#6b5d48' }}>
            Cancel
          </button>
        </div>
      </div>

      {/* Confirm dialog */}
      {showConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1060,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#faf7f0', borderRadius: '16px', padding: '28px',
            maxWidth: '360px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.28)',
            border: '1.5px solid rgba(92,69,24,0.14)',
            fontFamily: '"Poppins", sans-serif',
          }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#f3f0e8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <i className="fa-solid fa-scissors" style={{ color: ACCENT, fontSize: '18px' }} />
            </div>
            <h4 style={{ textAlign: 'center', fontSize: '16px', fontWeight: 700, color: '#2a2418', margin: '0 0 8px' }}>
              Use This Crop?
            </h4>
            <p style={{ textAlign: 'center', fontSize: '12px', color: '#8a7d6a', margin: '0 0 6px' }}>
              {duration > 0
                ? `${formatTime(Math.floor(cropStart))} → ${formatTime(Math.floor(cropEnd))} · ${formatTime(Math.floor(cropDur))}`
                : 'Selected region will replace the current recording'}
            </p>
            <p style={{ textAlign: 'center', fontSize: '11px', color: '#a0927d', margin: '0 0 24px' }}>
              Original recording will be discarded
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={confirmGenerating}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1.5px solid #e4e0d8', background: '#fff', fontSize: '14px', fontWeight: 600, color: '#555', cursor: confirmGenerating ? 'not-allowed' : 'pointer', fontFamily: '"Poppins", sans-serif' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCrop}
                disabled={confirmGenerating}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', background: ACCENT, fontSize: '14px', fontWeight: 600, color: '#fff', cursor: confirmGenerating ? 'not-allowed' : 'pointer', fontFamily: '"Poppins", sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              >
                {confirmGenerating ? <><i className="fa-solid fa-spinner fa-spin" />Saving…</> : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnsiteAudioCropper;
